import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  inject,
  OnInit,
  DestroyRef
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import {
  Constraint,
  PolicyType,
  region,
  UseCaseId
} from '@htx/policies';
import { regionOptions } from '../../../shared/data/region-options.data';
import { useCaseOptions } from '../../../shared/data/use-case-options.data';

/** Metadata for rendering a constraint type. */
interface TypeMeta {
  icon: string;
  labelKey: string;
  category: 'access' | 'time' | 'region' | 'compliance';
}

const TYPE_META: Record<string, TypeMeta> = {
  [PolicyType.PARTICIPANT_RESTRICTED]: { icon: 'people', labelKey: 'policyBuilder.type.participant.label', category: 'access' },
  [PolicyType.MEMBERSHIP_STATIC]: { icon: 'verified_user', labelKey: 'policyBuilder.type.membership.label', category: 'access' },
  [PolicyType.USE_CASE_MEMBERSHIP]: { icon: 'cases', labelKey: 'policyBuilder.type.useCase.label', category: 'access' },
  [PolicyType.FRAMEWORK_AGREEMENT]: { icon: 'handshake', labelKey: 'policyBuilder.type.frameworkAgreement.label', category: 'compliance' },
  [PolicyType.TIME_PERIOD_RESTRICTED]: { icon: 'hourglass_empty', labelKey: 'policyBuilder.type.timePeriod.label', category: 'time' },
  [PolicyType.END_DATE]: { icon: 'event', labelKey: 'policyBuilder.type.endDate.label', category: 'time' },
  [PolicyType.COMPANY_REGION_RESTRICTED]: { icon: 'domain', labelKey: 'policyBuilder.type.companyRegion.label', category: 'region' },
  [PolicyType.DATACENTER_REGION_RESTRICTED]: { icon: 'dns', labelKey: 'policyBuilder.type.datacenterRegion.label', category: 'region' }
};

@Component({
  selector: 'app-constraint-card',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatChipsModule,
    MatIconModule,
    MatIconButton,
    TranslocoPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './constraint-card.component.html',
  styleUrl: './constraint-card.component.scss'
})
export class ConstraintCardComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly i18n = inject(TranslocoService);

  /** The constraint to display/edit. */
  readonly constraint = input.required<Constraint>();

  /** Emits updated constraint when the user changes values. */
  readonly constraintChange = output<Constraint>();

  /** Emits when the user clicks the remove button. */
  readonly remove = output<void>();

  /** Expose PolicyType enum to template. */
  readonly PolicyType = PolicyType;

  readonly meta = computed<TypeMeta>(
    () => TYPE_META[this.constraint().type] ?? { icon: 'help', labelKey: 'Unknown', category: 'access' }
  );

  readonly regionOptions = regionOptions;
  readonly useCaseOptions = useCaseOptions;
  readonly minDate = new Date();

  // ─── Form Controls (per type) ────────────────────────────
  // Initialized lazily in ngOnInit based on constraint type.

  readonly bpnInput = new FormControl('');
  readonly bpnChips = signal<string[]>([]);

  readonly countCtrl = new FormControl<number | null>(null, [Validators.required, Validators.min(1)]);
  readonly regionCtrl = new FormControl<region | ''>('', Validators.required);
  readonly useCaseCtrl = new FormControl<UseCaseId | ''>('', Validators.required);
  readonly endDateCtrl = new FormControl<Date | null>(null, Validators.required);

  readonly isValid = computed(() => {
    const c = this.constraint();
    switch (c.type) {
      case PolicyType.PARTICIPANT_RESTRICTED:
        return this.bpnChips().length > 0;
      case PolicyType.MEMBERSHIP_STATIC:
      case PolicyType.FRAMEWORK_AGREEMENT:
        return true;
      case PolicyType.USE_CASE_MEMBERSHIP:
        return !!this.useCaseCtrl.value;
      case PolicyType.TIME_PERIOD_RESTRICTED:
        return this.countCtrl.valid;
      case PolicyType.END_DATE:
        return this.endDateCtrl.valid;
      case PolicyType.COMPANY_REGION_RESTRICTED:
      case PolicyType.DATACENTER_REGION_RESTRICTED:
        return !!this.regionCtrl.value;
      default:
        return true;
    }
  });

  ngOnInit(): void {
    const c = this.constraint();
    this.initFromConstraint(c);
    this.setupSubscriptions(c.type);
  }

  private initFromConstraint(c: Constraint): void {
    switch (c.type) {
      case PolicyType.PARTICIPANT_RESTRICTED:
        this.bpnChips.set(c.bpnNumbers ?? []);
        break;
      case PolicyType.TIME_PERIOD_RESTRICTED:
        this.countCtrl.setValue(c.count ?? null, { emitEvent: false });
        break;
      case PolicyType.USE_CASE_MEMBERSHIP:
        this.useCaseCtrl.setValue(c.useCaseId ?? '', { emitEvent: false });
        break;
      case PolicyType.END_DATE:
        this.endDateCtrl.setValue(c.endDate ?? null, { emitEvent: false });
        break;
      case PolicyType.COMPANY_REGION_RESTRICTED:
      case PolicyType.DATACENTER_REGION_RESTRICTED:
        this.regionCtrl.setValue(c.region ?? '', { emitEvent: false });
        break;
    }
  }

  private setupSubscriptions(type: PolicyType): void {
    switch (type) {
      case PolicyType.TIME_PERIOD_RESTRICTED:
        this.countCtrl.valueChanges.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => this.emitChange());
        break;

      case PolicyType.USE_CASE_MEMBERSHIP:
        this.useCaseCtrl.valueChanges.pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => this.emitChange());
        break;

      case PolicyType.END_DATE:
        this.endDateCtrl.valueChanges.pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => this.emitChange());
        break;

      case PolicyType.COMPANY_REGION_RESTRICTED:
      case PolicyType.DATACENTER_REGION_RESTRICTED:
        this.regionCtrl.valueChanges.pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => this.emitChange());
        break;
    }
  }

  // ─── BPN Chip Management ─────────────────────────────────

  addBpn(): void {
    const raw = this.bpnInput.value?.trim().replace(/\s/g, '') ?? '';
    if (!raw) return;
    const current = this.bpnChips();
    if (!current.includes(raw)) {
      this.bpnChips.set([...current, raw]);
      this.emitChange();
    }
    this.bpnInput.setValue('');
  }

  removeBpn(bpn: string): void {
    this.bpnChips.set(this.bpnChips().filter(b => b !== bpn));
    this.emitChange();
  }

  onBpnKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.addBpn();
    }
  }

  // ─── Emit Updated Constraint ─────────────────────────────

  private emitChange(): void {
    const c = this.buildConstraint();
    if (c) this.constraintChange.emit(c);
  }

  private buildConstraint(): Constraint | null {
    const type = this.constraint().type;
    switch (type) {
      case PolicyType.PARTICIPANT_RESTRICTED:
        return { type, bpnNumbers: this.bpnChips() };
      case PolicyType.MEMBERSHIP_STATIC:
      case PolicyType.FRAMEWORK_AGREEMENT:
        return { type };
      case PolicyType.USE_CASE_MEMBERSHIP:
        return this.useCaseCtrl.value
          ? { type, useCaseId: this.useCaseCtrl.value as UseCaseId }
          : null;
      case PolicyType.TIME_PERIOD_RESTRICTED:
        return this.countCtrl.value != null
          ? { type, count: this.countCtrl.value }
          : null;
      case PolicyType.END_DATE:
        return this.endDateCtrl.value
          ? { type, endDate: this.endDateCtrl.value }
          : null;
      case PolicyType.COMPANY_REGION_RESTRICTED:
        return this.regionCtrl.value
          ? { type, region: this.regionCtrl.value as region }
          : null;
      case PolicyType.DATACENTER_REGION_RESTRICTED:
        return this.regionCtrl.value
          ? { type, region: this.regionCtrl.value as region }
          : null;
      default:
        return null;
    }
  }
}
